import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Container,
  useTheme,
  alpha,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Avatar,
  LinearProgress,
  TablePagination
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Search,
  Schedule,
  CheckCircle,
  Warning,
  Star,
  Cancel,
  Info,
  GetApp
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import applicationService from '../../services/applicationService';
import jobPostingService from '../../services/jobPostingService';
import api from '../../services/api';

const Applications = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // State
  const [applications, setApplications] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    department: '',
    jobPosition: '',
    candidate: ''
  });
  const [departments, setDepartments] = useState([]);
  const [jobPositions, setJobPositions] = useState([]);
  const hasInitializedSearch = useRef(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, application: null });
  const [cvPreview, setCvPreview] = useState(null);
  const previewBlobUrlRef = useRef(null);
  const [previewLoadingKey, setPreviewLoadingKey] = useState(null);

  const revokePreviewUrl = useCallback(() => {
    if (previewBlobUrlRef.current) {
      URL.revokeObjectURL(previewBlobUrlRef.current);
      previewBlobUrlRef.current = null;
    }
  }, []);

  const closeCvPreview = useCallback(() => {
    revokePreviewUrl();
    setCvPreview(null);
  }, [revokePreviewUrl]);

  useEffect(() => {
    return () => revokePreviewUrl();
  }, [revokePreviewUrl]);

  const getDocumentErrorMessage = async (error) => {
    let message = error.message || 'Could not open file';
    if (error.response?.data instanceof Blob) {
      try {
        const t = await error.response.data.text();
        const j = JSON.parse(t);
        const prefix = j.code ? `[${j.code}] ` : '';
        message = prefix + (j.message || message);
      } catch (_) {
        message =
          'Could not open file. If the CV exists on the server, redeploy the backend; otherwise the file may be missing.';
      }
    } else if (error.response?.data?.message) {
      const d = error.response.data;
      const prefix = d.code ? `[${d.code}] ` : '';
      message = prefix + d.message;
    }
    return message;
  };

  const getApplicationDocumentItems = (app) => {
    const items = [];
    if (app.documents?.cv?.filename) {
      items.push({ kind: 'cv', label: 'CV (uploaded)' });
    }
    if (app.resume?.filename) {
      items.push({ kind: 'resume', label: 'Resume' });
    }
    if (app.coverLetterFile?.filename) {
      items.push({ kind: 'cover-letter', label: 'Cover letter file' });
    }
    if (app.portfolio?.filename) {
      items.push({ kind: 'portfolio', label: 'Portfolio' });
    }
    (app.additionalDocuments || []).forEach((doc, idx) => {
      if (doc?.filename) {
        items.push({
          kind: `additional-${idx}`,
          label: doc.description || `Attachment ${idx + 1}`
        });
      }
    });
    return items;
  };

  const handleDownloadApplicationDocument = async (applicationId, kind) => {
    try {
      const res = await applicationService.downloadApplicationDocument(applicationId, kind);
      const ct = (res.headers['content-type'] || '').toLowerCase();
      if (ct.includes('application/json')) {
        const text = await res.data.text();
        const err = JSON.parse(text);
        const prefix = err.code ? `[${err.code}] ` : '';
        throw new Error(prefix + (err.message || 'Download failed'));
      }
      applicationService.triggerBlobDownload(res, `${kind}.pdf`);
    } catch (error) {
      const message = await getDocumentErrorMessage(error);
      setSnackbar({
        open: true,
        message,
        severity: 'error'
      });
    }
  };

  /** Single GET — only when HR clicks View (no prefetch per row). */
  const handleViewApplicationDocument = async (application, kind, title) => {
    const applicationId = application._id;
    const key = `${applicationId}-${kind}`;
    const metaSaysPdf = applicationService.isApplicationDocumentLikelyPdf(application, kind);
    setPreviewLoadingKey(key);
    try {
      const res = await applicationService.downloadApplicationDocument(applicationId, kind);
      const ct = (res.headers['content-type'] || '').toLowerCase();
      if (ct.includes('application/json')) {
        const text = await res.data.text();
        const err = JSON.parse(text);
        const prefix = err.code ? `[${err.code}] ` : '';
        throw new Error(prefix + (err.message || 'Could not load file'));
      }
      const blob =
        res.data instanceof Blob ? res.data : new Blob([res.data], { type: ct || 'application/pdf' });
      const treatAsPdf =
        ct.includes('pdf') || (blob.type && blob.type.includes('pdf')) || metaSaysPdf;
      if (!treatAsPdf) {
        setSnackbar({
          open: true,
          message: 'Preview works for PDFs. Use Download for Word or other formats.',
          severity: 'info'
        });
        applicationService.triggerBlobDownload(res, `${kind}.pdf`);
        return;
      }
      revokePreviewUrl();
      const pdfBlob =
        blob.type && blob.type.includes('pdf')
          ? blob
          : new Blob([blob], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdfBlob);
      previewBlobUrlRef.current = url;
      setCvPreview({ url, title: title || 'Document' });
    } catch (error) {
      const message = await getDocumentErrorMessage(error);
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setPreviewLoadingKey(null);
    }
  };

  // Load applications
  const loadApplications = async () => {
    setLoading(true);
    try {
      const params = {};
      params.page = page + 1;
      params.limit = rowsPerPage;
      if (filters.status) params.status = filters.status;
      if (filters.manualStatus) params.manualStatus = filters.manualStatus;
      if (filters.search) params.search = filters.search;
      if (filters.department) params.department = filters.department;
      if (filters.jobPosition) params.position = filters.jobPosition;
      if (filters.candidate) params.candidate = filters.candidate;

      const response = await applicationService.getApplications(params);
      const payload = response.data || {};
      const apps = payload.docs || [];
      setTotalCount(payload.totalDocs || payload.total || 0);
      
      // Check for duplicate emails per job posting
      const duplicateEmails = [];
      const emailJobMap = new Map();
      
      apps.forEach(app => {
        const email = app.candidate?.email || app.personalInfo?.email;
        const jobId = app.jobPosting?._id;
        
        if (email && jobId) {
          const key = `${email}-${jobId}`;
          if (emailJobMap.has(key)) {
            duplicateEmails.push({
              email,
              jobTitle: app.jobPosting?.position?.title || app.jobPosting?.title,
              applicationIds: [emailJobMap.get(key), app._id]
            });
          } else {
            emailJobMap.set(key, app._id);
          }
        }
      });
      
      if (duplicateEmails.length > 0) {
        console.warn('Duplicate applications found:', duplicateEmails);
        setSnackbar({
          open: true,
          message: `Found ${duplicateEmails.length} duplicate applications. Please review.`,
          severity: 'warning'
        });
      }
      
      setApplications(apps);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error loading applications',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount and when filters change (search excluded — debounced below)
  useEffect(() => {
    loadApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadApplications + filters.search handled by debounced effect
  }, [filters.status, filters.manualStatus, filters.department, filters.jobPosition, filters.candidate, page, rowsPerPage]);

  const loadFilterOptions = useCallback(async () => {
    try {
      const [deptRes, jobsRes] = await Promise.all([
        api.get('/hr/departments'),
        jobPostingService.getJobPostings({ page: 1, limit: 500 })
      ]);

      if (deptRes.data?.success) {
        setDepartments(deptRes.data.data || []);
      }

      const postings = jobsRes?.data?.docs || [];
      const positionMap = new Map();
      postings.forEach((jp) => {
        const pos = jp.position;
        const posId = pos?._id || pos;
        if (!posId) return;
        const title = pos?.title || 'Untitled position';
        positionMap.set(String(posId), title);
      });
      const positions = Array.from(positionMap.entries())
        .map(([_id, title]) => ({ _id, title }))
        .sort((a, b) => a.title.localeCompare(b.title));
      setJobPositions(positions);
    } catch (err) {
      console.error('Failed to load application filter options:', err);
    }
  }, []);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  // Debounced search effect
  useEffect(() => {
    if (!hasInitializedSearch.current) {
      hasInitializedSearch.current = true;
      return;
    }

    const timeoutId = setTimeout(() => {
      if (filters.search !== undefined) {
        loadApplications();
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional debounce; omit loadApplications to avoid stale loop
  }, [filters.search]);

  // Handle status change
  const handleStatusChange = async (application, newStatus, reason = '') => {
    try {
      await applicationService.updateApplicationStatus(application._id, newStatus, reason);
      
      // If status is changed to "shortlisted", handle shortlisting process
      if (newStatus === 'shortlisted') {
        setSnackbar({
          open: true,
          message: 'Application shortlisted! Email sent to candidate and candidate record created/updated.',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Application status updated successfully',
          severity: 'success'
        });
      }
      
      loadApplications();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Error updating status',
        severity: 'error'
      });
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!deleteDialog.application) return;

    try {
      await applicationService.deleteApplication(deleteDialog.application._id);
      setSnackbar({
        open: true,
        message: 'Application deleted successfully',
        severity: 'success'
      });
      setDeleteDialog({ open: false, application: null });
      loadApplications();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Error deleting application',
        severity: 'error'
      });
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      applied: 'info',
      screening: 'warning',
      shortlisted: 'primary',
      interview_scheduled: 'warning',
      interviewed: 'primary',
      technical_test: 'warning',
      reference_check: 'info',
      offer_sent: 'success',
      offer_accepted: 'success',
      offer_declined: 'error',
      hired: 'success',
      rejected: 'error',
      withdrawn: 'default'
    };
    return colors[status] || 'default';
  };

  // Get manual status color
  const getManualStatusColor = (status) => {
    switch (status) {
      case 'shortlisted':
        return 'success';
      case 'rejected':
        return 'error';
      case 'under_review':
        return 'info';
      case 'interviewed':
        return 'warning';
      case 'offered':
        return 'primary';
      case 'hired':
        return 'success';
      case 'pending':
      default:
        return 'default';
    }
  };

  // Get manual status icon
  const getManualStatusIcon = (status) => {
    switch (status) {
      case 'shortlisted':
        return <Star fontSize="small" />;
      case 'rejected':
        return <Cancel fontSize="small" />;
      case 'under_review':
        return <Info fontSize="small" />;
      case 'interviewed':
        return <Schedule fontSize="small" />;
      case 'offered':
        return <CheckCircle fontSize="small" />;
      case 'hired':
        return <CheckCircle fontSize="small" />;
      case 'pending':
      default:
        return <Warning fontSize="small" />;
    }
  };

  // Get initials for avatar
  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  // Format date
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get days since application
  const getDaysSinceApplication = (createdAt) => {
    const days = applicationService.calculateDaysSinceApplication(createdAt);
    if (days === 0) return 'Today';
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  };

  return (
    <Container maxWidth="xl">
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
          Applications
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Track application progress and manage the hiring workflow
        </Typography>
      </Box>

      {/* Actions and Filters */}
      <Card sx={{ mb: 3, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                startIcon={<Add />}
                fullWidth
                onClick={() => navigate('/hr/talent-acquisition/applications/new')}
              >
                Create Application
              </Button>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search applications..."
                value={filters.search || ''}
                onChange={(e) => {
                  setPage(0);
                  setFilters(prev => ({ ...prev, search: e.target.value }));
                }}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => {
                    setPage(0);
                    setFilters(prev => ({ ...prev, status: e.target.value }));
                  }}
                  label="Status"
                  sx={{
                    '& .MuiSelect-select': {
                      paddingRight: '32px', // Ensure space for dropdown icon
                    },
                    '& .MuiSelect-icon': {
                      right: '8px', // Position icon properly
                    }
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="pending">Pending Review</MenuItem>
                  <MenuItem value="under_review">Under Review</MenuItem>
                  <MenuItem value="shortlisted">Shortlisted</MenuItem>
                  <MenuItem value="interviewed">Interviewed</MenuItem>
                  <MenuItem value="offered">Offered</MenuItem>
                  <MenuItem value="hired">Hired</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Manual Status</InputLabel>
                <Select
                  value={filters.manualStatus || ''}
                  onChange={(e) => {
                    setPage(0);
                    setFilters(prev => ({ ...prev, manualStatus: e.target.value }));
                  }}
                  label="Manual Status"
                  sx={{
                    '& .MuiSelect-select': {
                      paddingRight: '32px', // Ensure space for dropdown icon
                    },
                    '& .MuiSelect-icon': {
                      right: '8px', // Position icon properly
                    }
                  }}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="pending">Pending Review</MenuItem>
                  <MenuItem value="under_review">Under Review</MenuItem>
                  <MenuItem value="shortlisted">Shortlisted</MenuItem>
                  <MenuItem value="interviewed">Interviewed</MenuItem>
                  <MenuItem value="offered">Offered</MenuItem>
                  <MenuItem value="hired">Hired</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  value={filters.department}
                  onChange={(e) => {
                    setPage(0);
                    setFilters((prev) => ({ ...prev, department: e.target.value }));
                  }}
                  label="Department"
                >
                  <MenuItem value="">All Departments</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept._id} value={dept._id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Job Position</InputLabel>
                <Select
                  value={filters.jobPosition}
                  onChange={(e) => {
                    setPage(0);
                    setFilters((prev) => ({ ...prev, jobPosition: e.target.value }));
                  }}
                  label="Job Position"
                >
                  <MenuItem value="">All Positions</MenuItem>
                  {jobPositions.map((pos) => (
                    <MenuItem key={pos._id} value={pos._id}>
                      {pos.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      {loading && (
        <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />
      )}

      {/* Applications Table */}
      <Card>
        <CardContent>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                  <TableCell><strong>Application ID</strong></TableCell>
                  <TableCell><strong>Candidate</strong></TableCell>
                  <TableCell><strong>Department</strong></TableCell>
                  <TableCell><strong>Job Position</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Manual Status</strong></TableCell>
                  <TableCell><strong>Applied Date</strong></TableCell>
                  <TableCell><strong>Expected Salary</strong></TableCell>
                  <TableCell><strong>Availability</strong></TableCell>
                  <TableCell><strong>Documents</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {applications.map((application) => (
                  <TableRow key={application._id} hover>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold" color="primary">
                        {application.applicationId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                          {getInitials(
                            application.candidate?.firstName, 
                            application.candidate?.lastName
                          )}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {application.candidate?.fullName || 
                             `${application.candidate?.firstName} ${application.candidate?.lastName}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {application.candidate?.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {application.jobPosting?.department?.name || 'Not specified'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {application.jobPosting?.position?.title ||
                            application.jobPosting?.title ||
                            'Not specified'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {application.jobPosting?.jobCode || '—'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={application.statusLabel || application.status}
                        color={getStatusColor(application.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {application.evaluation?.manualStatus ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={application.evaluation.manualStatus.replace('_', ' ').toUpperCase()}
                            color={getManualStatusColor(application.evaluation.manualStatus)}
                            size="small"
                            icon={getManualStatusIcon(application.evaluation.manualStatus)}
                          />
                          {application.evaluation.manualStatusReason && (
                            <Tooltip title={application.evaluation.manualStatusReason}>
                              <Info sx={{ color: 'text.secondary', fontSize: 16 }} />
                            </Tooltip>
                          )}
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Warning sx={{ color: 'warning.main', fontSize: 20 }} />
                          <Chip
                            label="PENDING REVIEW"
                            color="warning"
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {formatDate(application.createdAt)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {getDaysSinceApplication(application.createdAt)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {application.expectedSalary ? 
                          applicationService.formatExpectedSalary(application.expectedSalary) : 
                          application.professionalInfo?.expectedSalary ?
                          applicationService.formatExpectedSalary(application.professionalInfo.expectedSalary) :
                          'Not specified'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={application.availabilityLabel || 
                               application.availability || 
                               application.professionalInfo?.availability || 
                               'Not specified'}
                        color="info"
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const docs = getApplicationDocumentItems(application);
                        if (docs.length === 0) {
                          return (
                            <Typography variant="caption" color="text.secondary">
                              None on file
                            </Typography>
                          );
                        }
                        return (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
                            {docs.map((d) => {
                              const rowKey = `${application._id}-${d.kind}`;
                              const loadingPreview = previewLoadingKey === rowKey;
                              const canPreviewPdf =
                                !d.kind.startsWith('additional-') &&
                                applicationService.isApplicationDocumentLikelyPdf(application, d.kind);
                              return (
                                <Stack direction="row" spacing={0} key={d.kind} alignItems="center">
                                  {canPreviewPdf && (
                                    <Tooltip title="View (PDF)">
                                      <span>
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          disabled={loadingPreview}
                                          onClick={() =>
                                            handleViewApplicationDocument(application, d.kind, d.label)
                                          }
                                        >
                                          {loadingPreview ? (
                                            <CircularProgress size={18} color="inherit" />
                                          ) : (
                                            <Visibility fontSize="small" />
                                          )}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  )}
                                  <Tooltip title={`Download: ${d.label}`}>
                                    <IconButton
                                      size="small"
                                      color="inherit"
                                      disabled={loadingPreview}
                                      onClick={() =>
                                        handleDownloadApplicationDocument(application._id, d.kind)
                                      }
                                    >
                                      <GetApp fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              );
                            })}
                          </Stack>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/hr/talent-acquisition/applications/${application._id}`)}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/hr/talent-acquisition/applications/${application._id}/edit`)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Change Status">
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                              value={application.evaluation?.manualStatus || 'pending'}
                              onChange={(e) => handleStatusChange(application, e.target.value)}
                              displayEmpty
                              sx={{ fontSize: '0.75rem' }}
                            >
                              <MenuItem value="pending">Pending Review</MenuItem>
                              <MenuItem value="under_review">Under Review</MenuItem>
                              <MenuItem value="shortlisted">Shortlisted</MenuItem>
                              <MenuItem value="interviewed">Interviewed</MenuItem>
                              <MenuItem value="offered">Offered</MenuItem>
                              <MenuItem value="hired">Hired</MenuItem>
                              <MenuItem value="rejected">Rejected</MenuItem>
                            </Select>
                          </FormControl>
                        </Tooltip>
                        
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteDialog({ open: true, application })}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {applications.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                No applications found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Create your first application to get started
              </Typography>
            </Box>
          )}
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </CardContent>
      </Card>

      <Dialog
        open={Boolean(cvPreview)}
        onClose={closeCvPreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '90vh', maxHeight: 900 } }}
      >
        <DialogTitle>{cvPreview?.title || 'Document'}</DialogTitle>
        <DialogContent sx={{ p: 0, flex: 1, minHeight: 0 }}>
          {cvPreview?.url ? (
            <Box
              component="iframe"
              title={cvPreview.title}
              src={cvPreview.url}
              sx={{ width: '100%', height: '100%', minHeight: '70vh', border: 'none', display: 'block' }}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCvPreview}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, application: null })}
      >
        <DialogTitle>Delete Application</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete application "{deleteDialog.application?.applicationId}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, application: null })}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Applications; 